import { useFormikContext } from 'formik';
import isDeepEqual from 'lodash/isEqual';
import { type FC, useContext, useEffect, useRef } from 'react';

import { hasValue } from '@core/utils/equality';

import { SlabDrawerContext } from './SlabDrawerContext';
import type { DrawerEditTracker } from './useDrawerCloseGuard';

type Props = {
  onClose: () => void;
  editTracker?: DrawerEditTracker;
};

/** Routes the enclosing SlabDrawer's Esc and outside-click closes through `onClose`,
 * so a form inside the drawer can guard them the way it guards its own Cancel. */
export const DrawerCloseHandler: FC<Props> = ({ onClose, editTracker }) => {
  const { registerCloseHandler } = useContext(SlabDrawerContext);
  const { dirty, values } = useFormikContext<unknown>();

  const latestRef = useRef({ dirty, values });
  // Forms fill some fields in on open; only what differs from the values seen at the
  // user's first touch counts as their edit.
  const baselineRef = useRef<{ values: unknown } | null>(null);

  useEffect(() => {
    latestRef.current = { dirty, values };
  });

  useEffect(() => {
    registerCloseHandler({
      onClose,
      onUserInteraction: () => {
        baselineRef.current ??= { values: latestRef.current.values };
      },
    });
    return (): void => registerCloseHandler(null);
  }, [registerCloseHandler, onClose]);

  useEffect(() => {
    editTracker?.track(() => {
      const baseline = baselineRef.current;
      return (
        latestRef.current.dirty &&
        hasValue(baseline) &&
        !isDeepEqual(latestRef.current.values, baseline.values)
      );
    });
    return (): void => editTracker?.track(null);
  }, [editTracker]);

  return null;
};
